// Phone country configs and validation/normalization helpers — must match
// the server's server/src/common/constants/phone.ts.
//
// Strictness contract:
// - isValidPhone is STRICT — no separators allowed (matches the historic
//   register regex; the split PhoneInput UI only ever sends digits).
// - toE164 / splitPhone are LENIENT — they strip non-digits first so legacy
//   stored values like "012-345 6789" or "+60 12-345 6789" still parse.
//
// Parse precedence (disambiguates "0…" vs bare "[89]…" vs "60…"/"65…" without "+"):
// 1. "+" prefix → test both E.164 patterns
// 2. starts with "0" → Malaysian local only (Singapore has no trunk 0)
// 3. exactly 8 digits starting 8/9 → Singapore local
// 4. starts with "60"/"65" without "+" → prepend "+" and test E.164 (legacy stored)
// 5. else invalid

export type PhoneCountryCode = 'MY' | 'SG'

export interface PhoneCountryConfig {
  iso2: PhoneCountryCode
  dialCode: string
  label: string
  e164Re: RegExp
  localRe: RegExp
  localExample: string
  e164Example: string
}

// Malaysia — mobile 01x (9-10 digits after 60), landline 0x (9-10 digits after 60)
const MY_E164_RE = /^\+60(1\d{8,9}|[2-9]\d{8,9})$/
const MY_LOCAL_RE = /^0(1\d{8,9}|[2-9]\d{8,9})$/

// Singapore — 8-digit NSN, mobile starts 8 or 9, no trunk 0.
// Singtel/StarHub/M1/Simba share these ranges (number portability), so
// validation is by prefix range, not operator.
const SG_E164_RE = /^\+65[89]\d{7}$/
const SG_LOCAL_RE = /^[89]\d{7}$/

export const PHONE_COUNTRIES: Record<PhoneCountryCode, PhoneCountryConfig> = {
  MY: {
    iso2: 'MY',
    dialCode: '60',
    label: 'Malaysia',
    e164Re: MY_E164_RE,
    localRe: MY_LOCAL_RE,
    localExample: '012 345 6789',
    e164Example: '+60123456789',
  },
  SG: {
    iso2: 'SG',
    dialCode: '65',
    label: 'Singapore',
    e164Re: SG_E164_RE,
    localRe: SG_LOCAL_RE,
    localExample: '8123 4567',
    e164Example: '+6581234567',
  },
}

// Strict: digits and a single leading "+" only — no hyphens, spaces, or parentheses.
export function isValidPhone(value: string): boolean {
  if (typeof value !== 'string' || value === '') return false
  if (!/^\+?\d+$/.test(value)) return false
  return toE164(value) !== null
}

// Lenient: strip non-digits, resolve to canonical E.164 (+60…/+65…) or null.
export function toE164(value: string): string | null {
  if (typeof value !== 'string') return null
  const cleaned = value.replace(/\D/g, '')
  if (!cleaned) return null

  // +60… / +65… (or legacy 60…/65… without the plus)
  if (cleaned.startsWith('60')) {
    return MY_E164_RE.test(`+${cleaned}`) ? `+${cleaned}` : null
  }
  if (cleaned.startsWith('65')) {
    return SG_E164_RE.test(`+${cleaned}`) ? `+${cleaned}` : null
  }
  // Malaysian local (trunk 0)
  if (cleaned.startsWith('0')) {
    return MY_LOCAL_RE.test(cleaned) ? `+60${cleaned.slice(1)}` : null
  }
  // Singapore local — bare 8 digits starting 8/9
  if (SG_LOCAL_RE.test(cleaned)) {
    return `+65${cleaned}`
  }
  return null
}

// Normalize-or-passthrough — preserves sentinel values like "0000000000".
export function toE164OrRaw(value: string): string {
  return toE164(value) ?? value
}

// Lenient parse for UI prefill: stored value → { country, local } or null.
export function splitPhone(value: string): { country: PhoneCountryCode; local: string } | null {
  const e164 = toE164(value)
  if (!e164) return null
  if (e164.startsWith('+65')) {
    return { country: 'SG', local: e164.slice(3) }
  }
  // Render Malaysian numbers with their trunk 0 so the local input matches
  // what a user would type (0123456789, not 123456789).
  return { country: 'MY', local: `0${e164.slice(3)}` }
}
